import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import styled from 'styled-components/macro';
import { Row, Col } from 'react-grid-system';
import { Flex, Box, Button } from 'components/ui';
import futuraSpec from 'utils/futuraSpec.json';
import useFutura from 'hooks/useFutura';
import MixInput from './MixInput';
import ApproveERC20 from 'components/ApproveERC20';
import { futuraChainId, futuraContractAddress, platformCollectionIds } from 'utils/config';
import { notifyTx } from 'utils/notifications';
import RemixStudioHelp from './RemixStudioHelp';
import { formatEther } from 'ethers/lib/utils';
import RemixSuccess from './RemixSuccess';
import TempoSelect from './TempoSelect';
import RemixShuffleOptions from './RemixShuffleOptions';
import RemixPreview from './RemixPreview';
import RemixMatrix from './RemixMatrix';
import MintingDetails from './MintingDetails';
import { Item, Attribute, OwnedNft, MixSpecs } from 'utils/types';
import {
  getMixPieceData,
  getPieceDataFromTempoAndMixCode,
  simulateRemix,
  getMixCodeFromRemixForm,
  RemixData,
} from 'utils/futura';
import unique from 'lodash/uniq';
import { FUTURA_PALETTES, FUTURA_ATTRIBUTES } from '../../constants';
import { useWallet } from 'components/WalletContext';
import { BigNumber } from 'ethers';
import useFormHistory from 'hooks/useFormHistory';
import { fetchExternalCollectionItems } from 'utils/platformPublicApi';
import isNil from 'lodash/isNil';

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
`;

const Error = styled.div`
  margin: 16px 0;
  color: #e33030;
  background-color: #edb2b2;
  padding: 8px 16px;
  border-radius: 8px;
  display: inline-block;
`;

const Wrapper = styled(Box)<any>`
  opacity: ${props => (props.isDisabled ? '.3' : '1')};
  pointer-events: ${props => (props.isDisabled ? 'none' : 'auto')};
`;

const REMIX_PRICE_BURN_BOTH = '0';
const REMIX_PRICE_BURN_ONE = '2500000000000000';
const REMIX_PRICE_NO_BURN = '5000000000000000';
const REMIX_PRICE_MINTPASS_DISCOUNT = '3750000000000000';
const INSUFICIENT_ETH = 'execution reverted: ERC20: transfer amount exceeds balance';
const CLIP_IDS_ALREADY_TAKEN = 'execution reverted: Clip IDs already taken';

const getMixSpecs = (mixes: OwnedNft[], tokenIds: string[]): MixSpecs[] => {
  const mixSpecs: MixSpecs[] = [];

  for (let tokenId of tokenIds) {
    const mix = mixes.find(m => m.token_id === tokenId);
    if (mix) {
      const attributes = mix?.metadata?.attributes || {};
      mixSpecs.push({
        attributes,
        pieceData: getMixPieceData(attributes),
        palette:
          FUTURA_PALETTES.find(
            palette => palette.name === attributes[FUTURA_ATTRIBUTES.colorPalette]
          ) || FUTURA_PALETTES[0],
      });
    }
  }
  return mixSpecs;
};

const getMatchingItems = async (values, mixAAttributes = {}, mixBAttributes = {}) => {
  const attributes: Attribute[] = [];
  for (let layer of futuraSpec.layers) {
    for (let section of futuraSpec.sections) {
      const attrName = `${section.name} ${layer.name}`;
      attributes.push({
        name: attrName,
        value: values[attrName] === 'A' ? mixAAttributes[attrName] : mixBAttributes[attrName],
      });
    }
  }

  const { results } = await fetchExternalCollectionItems(platformCollectionIds.futura, attributes);
  return results;
};

interface RemixSuccessData {
  tokenId?: number;
  gasUsed?: number;
  transactionHash?: string;
  mixCode?: string;
  remixData?: RemixData;
}

interface RemixStudioProps {
  mixes: OwnedNft[];
  ownsMixpass: boolean;
}

const RemixStudio = ({ mixes, ownsMixpass }: RemixStudioProps) => {
  const { address } = useWallet();
  const { mintRemix, paymentToken } = useFutura();
  const [success, setSuccess] = useState<RemixSuccessData | null>(null);

  const validate = useCallback(
    async values => {
      const [mixASpecs, mixBSpecs] =
        values.mixA && values.mixB ? getMixSpecs(mixes, [values.mixA, values.mixB]) : [];
      if (values.mixA !== undefined && values.mixB !== undefined) {
        try {
          // Fetch items with this selection of clips
          const results: Item[] = await getMatchingItems(
            values,
            mixASpecs.attributes,
            mixBSpecs.attributes
          );

          if (results && results.length > 0) {
            // Check if the match is one of the burned items
            const burnAId = values.burnA ? values.mixA : null;
            const burnBId = values.burnB ? values.mixB : null;
            const matchId = results[0].token_id;

            if (matchId !== burnAId && matchId !== burnBId) {
              return {
                mixA: `{ "message": "This combination of clips already exists:", "title": "${results[0].attributes.title}", "id": "${matchId}" }`,
              };
            }
          }
        } catch (e: any) {
          console.error(e.data?.message);
        }
      }
      return {};
    },
    [mixes]
  );

  const submitRemix = async (values, { setErrors, setSubmitting }) => {
    setSubmitting(true);
    const mixCode = getMixCodeFromRemixForm(values, mixes);

    await notifyTx({
      method: async () =>
        mintRemix(
          parseInt(values.mixA),
          parseInt(values.mixB),
          values.tempoIndex,
          mixCode,
          Boolean(values.burnA),
          Boolean(values.burnB)
        ),
      chainId: futuraChainId,
      success: 'Your remix was created successfully!',
      error: 'An error occured while trying to remix',
      onSuccess: async receipt => {
        const transferEvent = receipt?.events?.find(
          e => e.event === 'Transfer' && e.args?.to === address
        );
        const tokenId = (transferEvent?.args?.tokenId as BigNumber)?.toNumber();
        const gasUsed = (receipt?.gasUsed as BigNumber)?.toNumber();
        if (transferEvent) {
          setSuccess({
            transactionHash: receipt?.transactionHash,
            tokenId,
            gasUsed,
            mixCode: remixCode,
            remixData: remix,
          });
        }
        setSubmitting(false);
        // TODO: issue with metadata not existing right away after mint. Need to wait for it to be indexed or fetch from CNFT
        // these queryClient calls may be needed but commenting out for now as the missing metadata was the actual cause of the issue
        // queryClient.invalidateQueries('owned');
        // queryClient.removeQueries('owned');
        return Promise.resolve();
      },
      onError: (error: any) => {
        setSubmitting(false);
        if (error?.data?.message === INSUFICIENT_ETH) {
          setErrors({
            mixA: '{ "message": "Insufficient funds." }',
          });
        } else if (error?.data?.message === CLIP_IDS_ALREADY_TAKEN) {
          setErrors({
            mixA: '{ "message": "This combination of clips already exists." }',
          });
        } else {
          console.error(error?.data?.message);
        }
      },
    });
  };

  const initialClips = {};
  for (let layer of futuraSpec.layers) {
    for (let section of futuraSpec.sections) {
      const attrKey = `${section.name} ${layer.name}`;
      initialClips[attrKey] = 'A';
    }
  }

  const initialValues = {
    mixA: undefined,
    mixB: undefined,
    tempoIndex: '0',
    burnA: undefined,
    burnB: undefined,
    ...initialClips,
  };
  const { values, errors, handleChange, setValues, handleSubmit, isSubmitting, isValid } =
    useFormik({
      initialValues,
      validate,
      onSubmit: submitRemix,
      validateOnMount: true,
    });

  const remixAnother = () => {
    if (values.burnA || values.burnB) {
      setValues(initialValues);
    }
    setSuccess(null);
  };

  const { undo, redo, push, canUndo, canRedo } = useFormHistory({ initialValues, setValues });

  const optionsA = values.mixB ? mixes.filter(mix => mix.token_id !== values.mixB) : mixes;
  const optionsB = values.mixA ? mixes.filter(mix => mix.token_id !== values.mixA) : mixes;

  let mintPrice = REMIX_PRICE_NO_BURN;

  if (values.burnA && values.burnB) {
    mintPrice = REMIX_PRICE_BURN_BOTH;
  } else if (values.burnA || values.burnB) {
    mintPrice = REMIX_PRICE_BURN_ONE;
  } else if (ownsMixpass) {
    mintPrice = REMIX_PRICE_MINTPASS_DISCOUNT;
  }

  const [mixASpecs, mixBSpecs] =
    values.mixA && values.mixB ? getMixSpecs(mixes, [values.mixA, values.mixB]) : [];
  const tempos = useMemo(() => {
    const options: number[] = [];

    if (!isNil(mixASpecs?.attributes.Bpm)) {
      options.push(parseInt(mixASpecs.attributes.Bpm));
    }

    if (!isNil(mixBSpecs?.attributes.Bpm)) {
      options.push(parseInt(mixBSpecs.attributes.Bpm));
    }

    return unique(options);
  }, [mixASpecs, mixBSpecs]);

  const mixesSelected = values.mixA && values.mixB;
  const selectedMixCode = mixesSelected ? getMixCodeFromRemixForm(values, mixes) : null;
  const remix = simulateRemix(mixASpecs?.attributes, mixBSpecs?.attributes, selectedMixCode || []);
  const remixTempo = futuraSpec.tempo[values.tempoIndex];
  const remixCode = selectedMixCode?.join(',') || '';

  const pieceData = getPieceDataFromTempoAndMixCode(remixTempo, remixCode);

  if (success !== null) {
    return (
      <RemixSuccess
        preview={
          <RemixPreview
            id="remix_preview"
            mixCode={success.mixCode}
            remixData={success.remixData}
            tempo={remixTempo}
            showDetails={false}
          />
        }
        price={formatEther(mintPrice)}
        burnA={Boolean(values.burnA)}
        burnB={Boolean(values.burnB)}
        onClose={remixAnother}
        ownsMixpass={ownsMixpass}
        {...success}
      />
    );
  }

  const handleInputChange = e => {
    handleChange(e);
    push({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  let mainError = JSON.parse(errors?.mixA || '{}');

  return (
    <form onSubmit={handleSubmit}>
      <Row>
        <Col sm={12} md={6} lg={7}>
          <Box p={[3, 4]} maxWidth="1024px" margin="0 auto">
            <Box fontWeight="bold" mb={2} fontSize="24px">
              Remix Studio
            </Box>
            <Box fontWeight={1} fontSize="12px">
              Select your two Futura Mix input sources and layer selections to create a Futura
              Remix.
            </Box>

            <Flex mt={4} flexDirection={['column', 'row']} alignCenter>
              <MixInput
                name="mixA"
                label="A:"
                mixes={optionsA}
                value={values.mixA}
                onChange={handleInputChange}
                mr={[0, 3]}
              />
              <MixInput
                name="mixB"
                label="B:"
                mixes={optionsB}
                value={values.mixB}
                onChange={handleInputChange}
              />
            </Flex>

            <Wrapper isDisabled={!mixesSelected}>
              <Flex mt={[2, 4]} mb={3} flexDirection={['column', 'row']} alignCenter justifyEnd>
                <TempoSelect
                  name="tempoIndex"
                  value={values.tempoIndex}
                  options={tempos}
                  onChange={handleInputChange}
                />

                <RemixShuffleOptions
                  values={values}
                  setValues={setValues}
                  disabled={!mixesSelected}
                  canUndo={canUndo}
                  canRedo={canRedo}
                  onUndo={undo}
                  onRedo={redo}
                  push={push}
                />
              </Flex>
            </Wrapper>

            {mixesSelected && (
              <RemixMatrix
                pieceData={pieceData}
                mixASpecs={mixASpecs}
                mixBSpecs={mixBSpecs}
                handleChange={handleInputChange}
                push={push}
                values={values}
              />
            )}

            <RemixStudioHelp initialOpen={!Boolean(remix)} />
          </Box>
        </Col>

        <Col sm={12} md={6} lg={5}>
          <Box
            background="black"
            p={4}
            height="100%"
            minHeight={!mixesSelected ? 'calc(100vh - 192px)' : 'auto'}
          >
            <Box mb={2}>
              <Box fontSize="24px" fontWeight={1} mb={2}>
                Remix Preview
              </Box>
              <Box fontWeight={1} fontSize="12px">
                Review your Futura Remix audio, visuals and burn preferences before minting.
              </Box>
            </Box>

            <RemixPreview
              id="futura_remix"
              mixCode={selectedMixCode?.join(',')}
              remixData={remix}
              tempo={futuraSpec.tempo[values.tempoIndex]}
            />

            <Wrapper
              isDisabled={!mixesSelected}
              display={!mixesSelected ? 'none' : 'block'}
              backgroundColor="#121212"
              borderRadius="8px"
              my={2}
              px={3}
              py={2}
            >
              <MintingDetails />

              <Flex mt={2} mb={3}>
                <Flex mr={3} alignCenter>
                  <Box mr={2}>
                    <label htmlFor="burnA">🔥 Burn input A</label>
                  </Box>
                  <Checkbox
                    id="burnA"
                    type="checkbox"
                    name="burnA"
                    value={values.burnA}
                    onChange={handleChange}
                  />
                </Flex>
                <Flex alignCenter>
                  <Box mr={2}>
                    <label htmlFor="burnB">🔥 Burn input B</label>
                  </Box>
                  <Checkbox
                    id="burnB"
                    type="checkbox"
                    name="burnB"
                    value={values.burnB}
                    onChange={handleChange}
                  />
                </Flex>
              </Flex>

              {mainError?.message && (
                <Error>
                  {mainError.message}{' '}
                  {mainError.id && (
                    <Link to={`/futura/details/${mainError.id}`} target="_blank">
                      {mainError.title}
                    </Link>
                  )}
                </Error>
              )}

              {parseInt(mintPrice) > 0 ? (
                <ApproveERC20
                  amount={mintPrice}
                  paymentToken={paymentToken}
                  spendingContractAddress={futuraContractAddress}
                  approveLabel={`Approve WETH`}
                  chainId={futuraChainId}
                >
                  <Button disabled={isSubmitting || !isValid} type="submit">
                    Mint Remix Ξ{formatEther(mintPrice)}
                  </Button>
                </ApproveERC20>
              ) : (
                <Button type="submit" disabled={isSubmitting || !isValid}>
                  Mint Remix Ξ{formatEther(mintPrice)}
                </Button>
              )}

              {ownsMixpass && !values.burnA && !values.burnB && (
                <Box
                  p={2}
                  width="100%"
                  fontSize="14px"
                  backgroundColor="#444"
                  borderRadius="8px"
                  my={3}
                >
                  You're getting a 25% discount on your Remix because you own a MixPass.
                </Box>
              )}
            </Wrapper>
          </Box>
        </Col>
      </Row>
    </form>
  );
};

export default RemixStudio;
